import dayjs from "dayjs";
import dayjsBusinessDays from "dayjs-business-days";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);
dayjs.extend(timezone);

const EASTERN_TZ = "America/New_York";
const MARKET_CLOSE_HOUR = 16;

/**
 * Convert an instant to America/New_York, keeping the real clock time.
 * The UI shows local time; pricing math runs in Eastern.
 * @param {dayjs.Dayjs|string|Date|null} date
 * @returns {dayjs.Dayjs}
 */
export function toEastern(date = null) {
  return (date ? dayjs(date) : dayjs()).tz(EASTERN_TZ);
}

/**
 * Pin a timestamp to 16:00 America/New_York (regular-session close) on that
 * instant's Eastern calendar date. Used for future mark prices, not "now".
 * @param {dayjs.Dayjs|string|Date|null} date
 * @returns {dayjs.Dayjs}
 */
export function atEasternMarketClose(date = null) {
  return toEastern(date)
    .hour(MARKET_CLOSE_HOUR)
    .minute(0)
    .second(0)
    .millisecond(0);
}

/**
 * 16:00 ET on the given calendar date (YYYY-MM-DD digits as an Eastern
 * trading date). Use for date-only values like an expiration YYYYMMDD or
 * a date-picker day — do not convert a local-midnight instant to ET,
 * which would roll back a day.
 * @param {dayjs.Dayjs|string|Date} date
 * @returns {dayjs.Dayjs}
 */
export function easternMarketCloseOnCalendarDate(date) {
  const ymd = dayjs(date).format("YYYY-MM-DD");
  return dayjs.tz(`${ymd} ${MARKET_CLOSE_HOUR}:00:00`, EASTERN_TZ);
}

/**
 * Remaining session closes after the Eastern date of `customDate` (or now).
 * Each returned instant is 16:00 ET — these are the future mark prices.
 * Today's close is excluded; the entry price is computed at the actual now.
 * @param {dayjs.Dayjs} expirationDate - Option expiration date (16:00 ET)
 * @param {dayjs.Dayjs} customDate - Actual "now" instant (optional)
 * @returns {dayjs.Dayjs[]} - Array of trading-day closes
 */
export function getTradingDaysUntilExpiration(
  expirationDate,
  customDate = null,
) {
  const today = atEasternMarketClose(customDate);
  const tradingDays = [];

  let currentDay = today.add(1, "day");

  // Loop through all days until expiration
  while (
    currentDay.isBefore(expirationDate) ||
    currentDay.isSame(expirationDate, "day")
  ) {
    // Check if it's a business day (Monday-Friday, excluding holidays)
    if (currentDay.isBusinessDay()) {
      tradingDays.push(currentDay);
    }
    currentDay = currentDay.add(1, "day");
  }
  return tradingDays;
}

/**
 * Calculate number of trading days between two dates
 * @param {dayjs.Dayjs} startDate - Start date
 * @param {dayjs.Dayjs} endDate - End date
 * @returns {number} - Number of trading days
 */
export function countTradingDays(startDate, endDate) {
  return getTradingDaysUntilExpiration(endDate).filter((day) =>
    day.isSameOrAfter(startDate, "day"),
  ).length;
}

/**
 * Calculate days to expiration (calendar days)
 * @param {dayjs.Dayjs} tradingDay - Current trading day
 * @param {dayjs.Dayjs} expirationDate - Expiration date
 * @returns {number} - Calendar days to expiration
 */
export function getDaysToExpiration(tradingDay, expirationDate) {
  // Both sides at 16:00 ET so the diff is whole sessions. Mixing a tz-aware
  // Eastern close with a local-midnight expiration truncates the last one
  // or two days to 0.
  return easternMarketCloseOnCalendarDate(expirationDate).diff(
    atEasternMarketClose(tradingDay),
    "day",
  );
}

/**
 * Time to expiration in years from a real instant (the "now" / entry price).
 * `fromDate` is shown in local time in the UI; this converts it to Eastern
 * and measures until the expiration's 16:00 ET close. Fractional — not
 * snapped to a session close — so 10:08 ET is not treated as 16:00 ET.
 * @param {dayjs.Dayjs|string|Date|null} fromDate
 * @param {dayjs.Dayjs} expirationDate
 * @returns {number}
 */
export function getYearsToExpirationFromNow(fromDate, expirationDate) {
  const fromEt = toEastern(fromDate);
  const expirationClose = easternMarketCloseOnCalendarDate(expirationDate);
  const fractionalDays = expirationClose.diff(fromEt, "day", true);
  return daysToYears(Math.max(0, fractionalDays));
}

/**
 * Calculate time to expiration in years (for Black-Scholes)
 * @param {number} daysToExpiration - Calendar days to expiration
 * @returns {number} - Time in years
 */
export function daysToYears(daysToExpiration) {
  return daysToExpiration / 365;
}
